import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Star, StarOff, TrendingUp, TrendingDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const StockDetail = () => {
  const { ticker } = useParams();
  const { isAuthenticated, addToWatchlist, removeFromWatchlist, isInWatchlist } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isWatched = isInWatchlist(ticker);

  useEffect(() => {
    fetchStockAnalysis();
  }, [ticker]);

  const fetchStockAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/stocks/analyze/${ticker}`);
      setAnalysis(response.data.analysis);
    } catch (error) {
      console.error('Error fetching stock analysis:', error);
      setError(error.response?.data?.message || 'Failed to load stock analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleWatchlistToggle = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to use watchlist');
      return;
    }
    
    if (isWatched) {
      await removeFromWatchlist(ticker);
    } else {
      await addToWatchlist(ticker);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-danger-600 mb-4">
          <TrendingDown className="h-12 w-12 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Error Loading Analysis
        </h3>
        <p className="text-gray-600 mb-6">{error}</p>
        <div className="space-x-4">
          <button
            onClick={fetchStockAnalysis}
            className="btn-primary"
          >
            Try Again
          </button>
          <Link to="/" className="btn-secondary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No Analysis Available
        </h3>
        <p className="text-gray-600 mb-6">
          Unable to load analysis for {ticker.toUpperCase()}
        </p>
        <Link to="/" className="btn-primary">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  };

  const formatPercent = (percent) => {
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/"
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {analysis.ticker}
            </h1>
            <p className="text-gray-600">{analysis.companyName}</p>
          </div>
        </div>

        {isAuthenticated && (
          <button
            onClick={handleWatchlistToggle}
            className="btn-secondary flex items-center space-x-2"
          >
            {isWatched ? (
              <Star className="h-4 w-4 text-warning-500 fill-current" />
            ) : (
              <StarOff className="h-4 w-4" />
            )}
            <span>{isWatched ? 'Remove from Watchlist' : 'Add to Watchlist'}</span>
          </button>
        )}
      </div>

      {/* Price and Basic Info */}
      <div className="card">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-600">Current Price</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatPrice(analysis.currentPrice)}
            </p>
          </div>
          
          <div>
            <p className="text-sm text-gray-600">Day Change</p>
            <p className={`text-2xl font-bold flex items-center ${
              analysis.dayChangePercent >= 0 ? 'text-success-600' : 'text-danger-600'
            }`}>
              {analysis.dayChangePercent >= 0 ? (
                <TrendingUp className="h-5 w-5 mr-1" />
              ) : (
                <TrendingDown className="h-5 w-5 mr-1" />
              )}
              {formatPercent(analysis.dayChangePercent)}
            </p>
          </div>
          
          <div>
            <p className="text-sm text-gray-600">Volume</p>
            <p className="text-2xl font-bold text-gray-900">
              {analysis.marketData.volume.toLocaleString()}
            </p>
          </div>
          
          <div>
            <p className="text-sm text-gray-600">Market Cap</p>
            <p className="text-2xl font-bold text-gray-900">
              ${(analysis.marketData.marketCap / 1000000000).toFixed(1)}B
            </p>
          </div>
        </div>
      </div>

      {/* Recommendation */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Recommendation</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <span className={`badge text-lg px-4 py-2 ${
                analysis.recommendation.action === 'BUY' ? 'badge-success' :
                analysis.recommendation.action === 'SELL' ? 'badge-danger' : 'badge-neutral'
              }`}>
                {analysis.recommendation.action}
              </span>
              <span className="text-lg font-medium">
                {analysis.recommendation.confidence}% Confidence
              </span>
            </div>
            <p className="text-gray-700 leading-relaxed">
              {analysis.recommendation.reasoning}
            </p>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Entry Price:</span>
              <span className="font-medium">{formatPrice(analysis.recommendation.entryPrice)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Target Price:</span>
              <span className="font-medium text-success-600">{formatPrice(analysis.recommendation.targetPrice)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Stop Loss:</span>
              <span className="font-medium text-danger-600">{formatPrice(analysis.recommendation.stopLoss)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Time Horizon:</span>
              <span className="font-medium capitalize">{analysis.recommendation.timeHorizon}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Technical Analysis */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Technical Analysis</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">RSI (14)</h3>
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    analysis.technicalIndicators.rsi < 30 ? 'bg-success-500' :
                    analysis.technicalIndicators.rsi > 70 ? 'bg-danger-500' : 'bg-warning-500'
                  }`}
                  style={{ width: `${analysis.technicalIndicators.rsi}%` }}
                />
              </div>
              <span className="font-medium">{analysis.technicalIndicators.rsi.toFixed(1)}</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {analysis.technicalIndicators.rsi < 30 ? 'Oversold' :
               analysis.technicalIndicators.rsi > 70 ? 'Overbought' : 'Neutral'}
            </p>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-2">MACD</h3>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>MACD:</span>
                <span className={analysis.technicalIndicators.macd.macd > 0 ? 'text-success-600' : 'text-danger-600'}>
                  {analysis.technicalIndicators.macd.macd.toFixed(4)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Signal:</span>
                <span>{analysis.technicalIndicators.macd.signal.toFixed(4)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Histogram:</span>
                <span className={analysis.technicalIndicators.macd.histogram > 0 ? 'text-success-600' : 'text-danger-600'}>
                  {analysis.technicalIndicators.macd.histogram.toFixed(4)}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-2">Volume Analysis</h3>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>Current:</span>
                <span>{analysis.technicalIndicators.volume.current.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Average:</span>
                <span>{analysis.technicalIndicators.volume.average.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Ratio:</span>
                <span className={analysis.technicalIndicators.volume.ratio > 1.5 ? 'text-success-600' : 'text-gray-900'}>
                  {analysis.technicalIndicators.volume.ratio.toFixed(1)}x
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Assessment */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Risk Assessment</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className={`text-2xl font-bold ${
                analysis.riskAssessment.overallScore <= 3 ? 'text-success-600' :
                analysis.riskAssessment.overallScore <= 6 ? 'text-warning-600' : 'text-danger-600'
              }`}>
                {analysis.riskAssessment.overallScore}/10
              </div>
              <div>
                <p className="font-medium">Overall Risk Score</p>
                <p className="text-sm text-gray-600">
                  {analysis.riskAssessment.overallScore <= 3 ? 'Low Risk' :
                   analysis.riskAssessment.overallScore <= 6 ? 'Medium Risk' : 'High Risk'}
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Volatility:</span>
                <span>{(analysis.riskAssessment.volatility * 100).toFixed(2)}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Liquidity:</span>
                <span className="capitalize">{analysis.riskAssessment.liquidity}</span>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Risk Factors</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {analysis.riskAssessment.factors.map((factor, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                    factor.severity <= 3 ? 'bg-success-500' :
                    factor.severity <= 6 ? 'bg-warning-500' : 'bg-danger-500'
                  }`} />
                  <div className="flex-1">
                    <p className="text-sm text-gray-700">{factor.description}</p>
                    <p className="text-xs text-gray-500 capitalize">
                      {factor.type} • {factor.impact} impact • Severity: {factor.severity}/10
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Trading Windows */}
      {analysis.tradingWindows && analysis.tradingWindows.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Optimal Trading Windows</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {analysis.tradingWindows.map((window, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="text-center">
                  <p className="font-medium text-gray-900">
                    {window.startTime} - {window.endTime}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {(window.probability * 100).toFixed(0)}% probability
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Expected move: {(window.expectedMove * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent News */}
      {analysis.news && analysis.news.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Recent News</h2>
          <div className="space-y-4">
            {analysis.news.map((newsItem, index) => (
              <div key={index} className="border-b border-gray-200 pb-4 last:border-b-0">
                <h3 className="font-medium text-gray-900 mb-2">{newsItem.headline}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{newsItem.source}</span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    newsItem.sentiment === 'positive' ? 'bg-success-100 text-success-700' :
                    newsItem.sentiment === 'negative' ? 'bg-danger-100 text-danger-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {newsItem.sentiment}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StockDetail;